import { Box, Flex } from '@withjoy/joykit';
import React from 'react';

import { pxToRem } from '@withjoy/joykit/theme';
import type { DistributionChartProps } from '../../StatsSummary.types';
import { SkeletonText } from '@shared/components/Skeleton';
import { chartGroupStyles, rowContainerStles, rowProgressBarStyles } from './DistributionChart.styles';

const Container: React.FC = props => {
  return (
    <Flex
      flexDirection={'column'}
      rowGap={pxToRem(20)}
      flex={1}
      maxWidth={368}
    >
      {props.children}
    </Flex>
  );
};

type RowProps = {
  startLabel: React.ReactNode;
  endLabel: React.ReactNode;
  percentage: number;
} & Pick<DistributionChartProps, 'leadingColumnSizing'>;

const Row = ({ startLabel, endLabel, percentage, leadingColumnSizing }: RowProps) => {
  return (
    <Flex __css={rowContainerStles}>
      <Box
        as="span"
        __css={{ width: 56, flex: leadingColumnSizing === 'default' ? 0.75 : undefined, typographyVariant: 'body1' }}
      >
        {startLabel}
      </Box>
      <Box
        __css={rowProgressBarStyles}
        _after={{
          width: `${percentage * 100}%`,
        }}
      />
      <Box
        as="span"
        __css={{ flex: 0.5, width: 7, typographyVariant: 'body1' }}
      >
        {endLabel}
      </Box>
    </Flex>
  );
};

Row.Skeleton = () => {
  return (
    <Flex
      data-type="row"
      __css={{
        typographyVariant: 'label1',
        alignItems: 'center',
        columnGap: [3, 5],
      }}
    >
      <SkeletonText
        rows={1}
        width={7}
        skeletonHeight={14}
      />
      <SkeletonText
        rows={1}
        width={280}
        skeletonHeight={14}
      />
      <SkeletonText
        rows={1}
        width={5}
        skeletonHeight={14}
      />
    </Flex>
  );
};

export const DistributionChart = (props: DistributionChartProps) => {
  const { items, leadingColumnSizing = 'default' } = props;

  if (!items.length) {
    return null;
  }
  return (
    <Container>
      {items.map(({ id, startLabel, endLabel, percentage }) => (
        <Row
          key={id}
          startLabel={startLabel}
          endLabel={endLabel}
          percentage={percentage}
          leadingColumnSizing={leadingColumnSizing}
        />
      ))}
    </Container>
  );
};

DistributionChart.Skeleton = () => {
  return (
    <Container>
      <Row.Skeleton />
      <Row.Skeleton />
      <Row.Skeleton />
      <Row.Skeleton />
      <Row.Skeleton />
    </Container>
  );
};

export const DistributionChartGroup: React.FC = ({ children }) => {
  return <Flex __css={chartGroupStyles}>{children}</Flex>;
};
